import { jsonToBlob } from '@common/utils';
import { buildPageableParams, PageableArgs } from '../common';
import { queryDownloadFile } from '../common/pageable/query-download-file';
import { queryUntilLastPage } from '../common/pageable/query-until-last-page';
import { servicesApi } from '../services';
import { MotifMailCategorie, MotifMailDto, MotifMailProjection, SendEmailRelanceBody } from './types';

const MOTIF_MAILS_URL_PREFIX = 'motif-mails';

const motifMailApi = servicesApi.injectEndpoints!({
  endpoints: build => ({

    /**
     * Méthode qui envoie un email de relance simple
     */
    sendEmail: build.mutation<void, SendEmailRelanceArgs, any>({
      query: ({ body }) => ({
        method: 'POST',
        url: `${MOTIF_MAILS_URL_PREFIX}/send`,
        body,
      }),
    }),

    createMotif: build.mutation<void, MotifMailCreateArgs, any>({
      query: (motifCreate) => {
        const formData = new FormData();
        formData.set('data', jsonToBlob(motifCreate.motifMailDto));
        if (motifCreate?.fichier) {
          formData.set('fichier', motifCreate.fichier);
        }
        return ({
          method: 'POST',
          url: `${MOTIF_MAILS_URL_PREFIX}`,
          body: formData,
        });
      },
      invalidatesTags: ['MotifsMail'],
    }),

    updateMotif: build.mutation<void, MotifMailUpdateArgs, any>({
      query: (motifUpdateArgs) => {
        const formData = new FormData();
        formData.set('data', jsonToBlob(motifUpdateArgs.motifMailDto));
        if (motifUpdateArgs?.fichier) {
          formData.set('fichier', motifUpdateArgs.fichier);
        }
        return ({
          method: 'PATCH',
          url: `${MOTIF_MAILS_URL_PREFIX}/${motifUpdateArgs.id}`,
          params: { fichierNeedUpdate: motifUpdateArgs.fichierNeedUpdate },
          body: formData,
        });
      },
      invalidatesTags: ['MotifsMail'],
    }),

    getAllMotifMails: build.query<MotifMailDto[], GetAllMotifMailsQueryArgs, any>({
      queryFn: (pageArgs, _api, _extraOptions, baseQuery) => queryUntilLastPage<MotifMailDto>({
        baseQuery,
        url: MOTIF_MAILS_URL_PREFIX,
        params: buildPageableParams(pageArgs),
      }),
      providesTags: ['MotifsMail'],
    }),

    getMotifMailById: build.query<MotifMailDto, GetMotifMailByIdQueryArgs, any>({
      query: ({ id, projection }) => ({
        url: `${MOTIF_MAILS_URL_PREFIX}/${id}`,
        params: { projection },
      }),
      providesTags: ['MotifsMail'],
    }),

    deleteMotifMailById: build.mutation<void, DeleteMotifMailByIdQueryArgs, any>({
      query: ({ id }) => ({
        url: `${MOTIF_MAILS_URL_PREFIX}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MotifsMail'],
    }),

    downloadMotifMailFichier: build.mutation<void, DownloadMotifMailQueryArgs, any>({
      queryFn: ({ id }, _api, _extraOptions, baseQuery) => queryDownloadFile({
        baseQuery,
        url: `${MOTIF_MAILS_URL_PREFIX}/${id}/fichier/resource`,
      }),
    }),
  }),
});

interface SendEmailRelanceArgs {
  body: SendEmailRelanceBody;
}

export interface DeleteMotifMailByIdQueryArgs {
  id: number;
}

export interface DownloadMotifMailQueryArgs {
  id: number;
}

interface GetAllMotifMailsQueryArgs extends Omit<PageableArgs<MotifMailProjection>, 'page'> {
}

interface GetMotifMailByIdQueryArgs {
  id: number;
  projection?: MotifMailProjection;
}

export interface MotifMailUpdateArgs {
  id: number;
  motifMailDto: MotifMailCreateOrUpdateDto;
  fichier: File | null;
  fichierNeedUpdate: boolean;
}

export interface MotifMailCreateArgs {
  motifMailDto: MotifMailCreateOrUpdateDto;
  fichier: File | null;
}

export interface MotifMailCreateOrUpdateDto {
  nom: string;
  sujet: string;
  corps: string;
  categorie: MotifMailCategorie;
}

export const {
  useSendEmailMutation,
  useGetAllMotifMailsQuery,
  useDownloadMotifMailFichierMutation,
  useCreateMotifMutation,
  useUpdateMotifMutation,
  useDeleteMotifMailByIdMutation,
} = motifMailApi;